/**
 * components/ProfileList.tsx
 * Profile selection and management list
 *
 * Displays all risk profiles as tabs/cards with options to add, delete, and set default
 */

// 1. External dependencies
import React, { useState } from 'react';

// 2. Internal absolute imports
import { Chip, Tabs, Tab, Input, TextArea, Switch } from '@enfomentis/ui';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';

/**
 * Risk profile type from draft
 */
type RiskProfile = NonNullable<OnboardingDraft['trading']['riskConfiguration']>['profiles'][number];

/**
 * ProfileList props
 */
interface ProfileListProps {
  profiles: RiskProfile[];
  selectedProfileId: string | null;
  defaultProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  onAddProfile: () => void;
  onDeleteProfile: (profileId: string) => void;
  onSetDefault: (profileId: string | null) => void;
  onUpdateProfile: (profileId: string, updates: Partial<RiskProfile>) => void;
  canDelete: (profileId: string) => boolean;
}

/**
 * ProfileList component
 */
export const ProfileList: React.FC<ProfileListProps> = ({
  profiles,
  selectedProfileId,
  defaultProfileId,
  onSelectProfile,
  onAddProfile,
  onDeleteProfile,
  onSetDefault,
  onUpdateProfile,
  canDelete
}) => {
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleStartEdit = (profile: RiskProfile) => {
    setEditingProfileId(profile.tempId);
    setEditingName(profile.name);
  };

  const handleSaveName = (profileId: string) => {
    if (editingName.trim()) {
      onUpdateProfile(profileId, { name: editingName.trim() });
    }
    setEditingProfileId(null);
    setEditingName('');
  };

  const handleCancelEdit = () => {
    setEditingProfileId(null);
    setEditingName('');
  };

  const selectedProfile = profiles.find(p => p.tempId === selectedProfileId);

  return (
    <div className="w-full space-y-4">
      {/* Tabs for profile selection with Add button as last tab */}
      <div className="overflow-x-auto scrollbar-hide">
        <Tabs
          aria-label="Risk profiles"
          selectedKey={selectedProfileId || ''}
          onSelectionChange={(key) => {
            if (key === '_add_profile') {
              onAddProfile();
            } else {
              onSelectProfile(key as string);
            }
          }}
          variant="underlined"
          fullWidth={false}
        >
        {profiles.map((profile) => (
          <Tab
            key={profile.tempId}
            title={
              <div className="flex items-center gap-2 w-auto">
                {/* Profile color indicator */}
                {profile.color && (
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: profile.color }}
                  />
                )}

                {/* Profile name - editable inline */}
                {editingProfileId === profile.tempId ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Input
                      size="sm"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveName(profile.tempId);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      autoFocus
                      className="w-32"
                    />
                    <div
                      className="p-1.5 rounded-md hover:bg-success-100 cursor-pointer transition-colors inline-flex items-center justify-center text-success text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveName(profile.tempId);
                      }}
                    >
                      ✓
                    </div>
                    <div
                      className="p-1.5 rounded-md hover:bg-default-100 cursor-pointer transition-colors inline-flex items-center justify-center text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEdit();
                      }}
                    >
                      ✕
                    </div>
                  </div>
                ) : (
                  <>
                    <span>{profile.name}</span>
                    <div
                      className="p-1.5 rounded-md hover:bg-default-100 cursor-pointer transition-colors inline-flex items-center justify-center text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(profile);
                      }}
                    >
                      ✎
                    </div>
                  </>
                )}

                {/* Default badge */}
                {profile.tempId === defaultProfileId && (
                  <Chip size="sm" variant="flat" color="primary">
                    Default
                  </Chip>
                )}

                {/* Delete button */}
                {canDelete(profile.tempId) && editingProfileId !== profile.tempId && (
                  <div
                    className="ml-2 p-1.5 rounded-md hover:bg-danger-100 cursor-pointer transition-colors inline-flex items-center justify-center text-danger text-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProfile(profile.tempId);
                    }}
                  >
                    ×
                  </div>
                )}
              </div>
            }
          />
        ))}

        {/* Add Profile Tab */}
        <Tab
          key="_add_profile"
          title={
            <div className="flex items-center gap-2 text-primary">
              <span>+</span>
              <span>Add Profile</span>
            </div>
          }
        />
      </Tabs>
      </div>

      {/* Desktop: side-by-side, Mobile: stacked */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
        {/* Profile description - editable */}
        {selectedProfile && (
          <>
            {/* Mobile: label outside (top) */}
            <div className="sm:hidden flex-1">
              <TextArea
                label="Description (Optional)"
                labelPlacement="outside"
                placeholder="e.g., 'Clean setup, no negative confluences'"
                value={selectedProfile.description || ''}
                onValueChange={(value) => {
                  onUpdateProfile(selectedProfile.tempId, { description: value });
                }}
                minRows={1}
                maxRows={3}
                size="sm"
                classNames={{
                  base: "w-full",
                  inputWrapper: "w-full"
                }}
              />
            </div>
            {/* Desktop: label outside-left */}
            <div className="hidden sm:block flex-1">
              <TextArea
                label="Description (Optional)"
                labelPlacement="outside-left"
                placeholder="e.g., 'Clean setup, no negative confluences'"
                value={selectedProfile.description || ''}
                onValueChange={(value) => {
                  onUpdateProfile(selectedProfile.tempId, { description: value });
                }}
                minRows={1}
                maxRows={3}
                size="sm"
                classNames={{
                  base: "w-full",
                  inputWrapper: "w-full"
                }}
              />
            </div>
          </>
        )}

        {/* Set as default switch */}
        {selectedProfileId && (
          <Switch
            isSelected={selectedProfileId === defaultProfileId}
            onValueChange={(isDefault) => {
              onSetDefault(isDefault ? selectedProfileId : null);
            }}
            size="sm"
          >
            <span className="text-sm">Set as Default</span>
          </Switch>
        )}
      </div>
    </div>
  );
};
